import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import client from '@api/client';
import { SERVICE_API } from '@api/endpoints';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useActiveServiceRequests(restaurantId) {
  return useQuery({
    queryKey: ['serviceRequests', 'active', restaurantId],
    queryFn: async () => {
      const { data } = await client.get(
        `${SERVICE_API}/restaurant/${restaurantId}/active`,
      );
      return data;
    },
    staleTime: 10_000,
    refetchInterval: 5_000,
    enabled: !!restaurantId,
  });
}

export function useServiceRequestHistory(params = {}) {
  const { restaurantId, page = 1, limit = 20, status, type, dateFrom, dateTo } = params;
  return useQuery({
    queryKey: ['serviceRequests', 'history', params],
    queryFn: async () => {
      const query = { page, limit };
      if (restaurantId) query.restaurantId = restaurantId;
      if (status) query.status = status;
      if (type) query.type = type;
      if (dateFrom) query.dateFrom = dateFrom;
      if (dateTo) query.dateTo = dateTo;
      const { data } = await client.get(SERVICE_API, { params: query });
      return data;
    },
    staleTime: 30_000,
    enabled: !!restaurantId,
  });
}

export function useServiceRequestStats(restaurantId) {
  return useQuery({
    queryKey: ['serviceRequests', 'stats', restaurantId],
    queryFn: async () => {
      const { data } = await client.get(
        `${SERVICE_API}/restaurant/${restaurantId}/statistics`,
      );
      return data;
    },
    staleTime: 30_000,
    enabled: !!restaurantId,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateServiceRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, tableId, restaurantId, message, guestName, priority }) => {
      const { data } = await client.post(SERVICE_API, {
        type,
        tableId,
        restaurantId,
        message,
        guestName,
        priority,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
    },
  });
}

export function useAcknowledgeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, restaurantId }) => {
      const { data } = await client.patch(
        `${SERVICE_API}/${id}/acknowledge`,
        {},
        { params: restaurantId ? { restaurantId } : {} },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
    },
  });
}

export function useStartRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, restaurantId }) => {
      const { data } = await client.patch(
        `${SERVICE_API}/${id}/start`,
        {},
        { params: restaurantId ? { restaurantId } : {} },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
    },
  });
}

export function useResolveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, restaurantId }) => {
      const { data } = await client.patch(
        `${SERVICE_API}/${id}/resolve`,
        {},
        { params: restaurantId ? { restaurantId } : {} },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
    },
  });
}

export function useCancelRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, restaurantId }) => {
      const { data } = await client.patch(
        `${SERVICE_API}/${id}/cancel`,
        {},
        { params: restaurantId ? { restaurantId } : {} },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceRequests'] });
    },
  });
}
